/**
 * CR (Change Request) Analysis — docs/flow/COMPASS_CR_IMPACT.md.
 *
 * Flow: enter a CR JIRA key → LISA fetches details + attachments (documents
 * incl. .msg, image OCR) and assembles the full CR text (≤200K chars) →
 * on the user's click, the browser sends it to Compass /change/analyze
 * (2–4 min, paid) → developer-grade impact analysis: six markdown sections
 * fully visible (no story card, no collapsed sections — per contract this
 * mode is for developers), a structured Change Plan table, and the
 * evidence accordion. Results persist per CR (one paid call; Re-run
 * overwrites).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Compass,
  Download,
  FileSearch,
  Loader2,
  Paperclip,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { CompassEvidenceAccordion } from '../common/CompassEvidence';
import { ChangeGraph, ChangePlanItem, CompassAnalysis, fetchChangeGraph, generateIad, getJob, pollJob, submitAnalysis } from '../../services/compassApi';
import SchemaGraphPage from '../schema-graph/SchemaGraphPage';
import { CrAttachmentList, CrListItem, CrRecord, deleteCr, fetchCr, getCr, listCrAttachments, listCrs } from '../../services/crAnalysisApi';
import { formatRelativeTime as formatTime, emailName } from '../../utils/dateFormat';
import { downloadAnalysisJson } from '../../utils/downloadJson';
import { CoverageNotice, CoverageItem, attachmentCoverage } from '../common/CoverageNotice';

const TECH_FONT =
  "Charter, 'Bitstream Charter', 'Sitka Text', Cambria, Georgia, serif";

/** The only JIRA project holding change requests — mirrors CR_PROJECT in
 *  the backend, which stays authoritative. Duplicated here only because it
 *  is a single stable constant; the Issue Analysis client list is NOT
 *  duplicated, because it changes whenever a customer is onboarded. */
const CR_PROJECT = "LRPP";

/** Rail page size — matches Issue Analysis so the two rails behave alike. */
const PAGE_SIZE = 10;

/** Hard cap on the text sent to Compass (backend: CR_TEXT_MAX_CHARS). */
const CR_TEXT_CAP = 200_000;

const CR_LIMITS = [
  'attachments: documents + images only (.doc not readable)',
  `assembled text capped at ${CR_TEXT_CAP.toLocaleString()} characters`,
  'spreadsheet sheets and rows are capped per file',
];

/** What the CR analysis actually read, from the record itself — so it can
 *  never claim coverage the stored data does not support. */
function crCoverage(record: CrRecord): CoverageItem[] {
  // One line per REASON — files skipped for hitting the document cap must
  // not be told to convert their format, which would change nothing.
  const items: CoverageItem[] = attachmentCoverage(record.extraction_results ?? []);

  const atCap = record.char_count >= CR_TEXT_CAP;
  items.push({
    label: 'Text analysed',
    value: `${record.char_count.toLocaleString()} chars`,
    incomplete: atCap,
    detail: atCap
      ? `The CR reached the ${CR_TEXT_CAP.toLocaleString()}-character limit, so its tail was not sent for analysis.`
      : undefined,
  });

  const plan = record.compass_response?.change_plan ?? [];
  if (plan.length > 0) {
    const unverified = plan.filter(
      (i) => (i.confidence || '').toLowerCase() !== 'confirmed',
    ).length;
    items.push({
      label: 'Change-plan items',
      value: `${plan.length} (${unverified} unconfirmed)`,
      incomplete: unverified > 0,
      detail:
        unverified > 0
          ? `${unverified} item(s) are leads the graph could not confirm — check them before relying on them.`
          : undefined,
    });
  }
  return items;
}

function fmtSize(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/** Attachment picker shown before a fresh JIRA pull. A year-old CR carries
 *  many superseded versions of the same document; the user picks what is
 *  still relevant, capped at the server's extraction budget. Names only —
 *  nothing is downloaded or scanned until Continue. */
function AttachmentSelectorModal({
  data,
  onContinue,
  onCancel,
}: {
  data: CrAttachmentList;
  onContinue: (ids: string[]) => void;
  onCancel: () => void;
}) {
  // Newest first — the whole point of the picker is skipping old versions.
  const sorted = [...data.attachments].sort((a, b) =>
    (b.created ?? '').localeCompare(a.created ?? ''),
  );
  const max = Math.max(1, data.max_selectable);
  const overLimit = sorted.length > max;
  // Within the budget the picker preselects everything (one click keeps the
  // old behaviour); over it, nothing — choosing is the point.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(overLimit ? [] : sorted.map((a) => a.id)),
  );
  const allTarget = Math.min(sorted.length, max);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < max) next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) =>
      prev.size > 0 ? new Set<string>() : new Set(sorted.slice(0, max).map((a) => a.id)),
    );

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-lg shadow-xl w-full flex flex-col pointer-events-auto"
          style={{ maxWidth: '85vw', width: 980, maxHeight: '85vh' }}
        >
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Paperclip size={15} className="text-purple-600" />
            <span className="text-sm font-semibold text-gray-900">
              {data.cr_key} — select attachments to analyse
            </span>
            <span className="text-xs text-gray-500 truncate">{data.summary}</span>
            <button
              onClick={onCancel}
              className="ml-auto p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
              title="Cancel (Esc)"
            >
              <X size={15} />
            </button>
          </div>

          <div className="px-4 py-2 flex items-center gap-3 text-xs text-gray-600 border-b border-gray-100">
            <span className="tabular-nums font-medium">
              {selected.size} of {sorted.length} selected (max {max})
            </span>
            {overLimit && (
              <span className="text-amber-700">
                This CR has {sorted.length} attachments — up to {max} can go into one
                analysis. Newest are listed first; Select all picks the newest {max}.
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            <table className="w-full table-fixed text-xs">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.size === allTarget && allTarget > 0}
                      onChange={toggleAll}
                      title={selected.size > 0 ? 'Clear selection' : `Select all (newest ${allTarget})`}
                      className="accent-purple-600 cursor-pointer"
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-semibold">Attachment</th>
                  <th className="w-24 px-2 py-2 text-right font-semibold">Size</th>
                  <th className="w-32 px-2 py-2 text-left font-semibold">Added</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => {
                  const checked = selected.has(a.id);
                  const disabled = !checked && selected.size >= max;
                  return (
                    <tr
                      key={a.id}
                      onClick={() => !disabled && toggle(a.id)}
                      className={`border-t border-gray-100 ${
                        disabled
                          ? 'opacity-40'
                          : `cursor-pointer ${checked ? 'bg-violet-50' : 'hover:bg-gray-50'}`
                      }`}
                    >
                      <td className="px-3 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          readOnly
                          className="accent-purple-600 pointer-events-none"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <span
                          className="block truncate font-mono text-[11px] text-gray-800"
                          title={a.filename}
                        >
                          {a.filename}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">
                        {fmtSize(a.size)}
                      </td>
                      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                        {a.created ? new Date(a.created).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onContinue(Array.from(selected))}
              disabled={selected.size === 0}
              className="px-3 py-1.5 text-sm font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              Continue with {selected.size} attachment{selected.size === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const CONFIDENCE_STYLE: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-800',
  likely: 'bg-amber-100 text-amber-800',
  unverified: 'bg-gray-100 text-gray-600',
};

/** Complexity rating — ONE definition, used by both the rail chip and the
 *  summary card, so the list can never contradict the page it opens.
 *  Blunt and explainable on purpose: blast radius OR item count. */
export function complexityOf(items: number, maxImpact: number) {
  if (maxImpact >= 500 || items >= 25)
    return { label: 'LARGE', filled: 4, dot: '●', chip: 'bg-red-50 text-red-700 border border-red-200' };
  if (maxImpact >= 50 || items >= 10)
    return { label: 'MEDIUM', filled: 3, dot: '●', chip: 'bg-amber-50 text-amber-700 border border-amber-200' };
  return { label: 'SMALL', filled: 2, dot: '●', chip: 'bg-green-50 text-green-700 border border-green-200' };
}

/** Business-tier summary — every number here is COUNTED from change_plan,
 *  never paraphrased by a model. It therefore cannot drift from the technical
 *  view below it, and cannot invent a risk that the graph did not measure. */
function deriveSummary(plan: ChangePlanItem[]) {
  const norm = (s: string) => (s || '').toLowerCase();
  const countKind = (k: string) => plan.filter((i) => norm(i.kind) === k).length;

  const mustChangeTogether = plan.filter((i) =>
    ['modify', 'widen'].includes(norm(i.action)),
  ).length;
  const additions = plan.filter((i) => norm(i.action) === 'add').length;
  const unverified = plan.filter((i) => norm(i.confidence) !== 'confirmed').length;

  const measured = plan.filter((i) => typeof i.impact_count === 'number');
  const biggest = measured.reduce<ChangePlanItem | null>(
    (best, i) => (!best || (i.impact_count ?? 0) > (best.impact_count ?? 0) ? i : best),
    null,
  );
  const maxImpact = biggest?.impact_count ?? 0;

  const scale = complexityOf(plan.length, maxImpact);

  return {
    scale,
    tables: countKind('table') + countKind('field'),
    procedures: countKind('procedure'),
    apps: countKind('application'),
    methods: countKind('method'),
    mustChangeTogether,
    additions,
    unverified,
    biggest,
    total: plan.length,
  };
}

/** Fills the IAD Word template from the analysis already on screen.
 *
 *  Does not re-analyse — it sends back the same three fields the page
 *  already holds. Disabled without a completed change plan, because a
 *  degraded analysis has nothing to build a document from. */
function GenerateIadButton({
  crKey,
  crText,
  explanation,
  changePlan,
  degraded,
}: {
  crKey: string;
  crText: string;
  explanation: string;
  changePlan: ChangePlanItem[] | null | undefined;
  degraded: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = !degraded && Array.isArray(changePlan) && changePlan.length > 0;

  const onClick = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await generateIad(crText, explanation, changePlan ?? []);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename === 'IAD.docx' ? `IAD_${crKey}.docx` : result.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  }, [crKey, crText, explanation, changePlan]);

  if (!ready) return null;

  return (
    <span className="relative inline-flex items-center">
      <button
        onClick={onClick}
        disabled={busy}
        title="Fill the IAD template from this analysis and download it"
        className="ml-2 px-2 py-0.5 text-[11px] font-medium rounded border border-purple-300 bg-white text-purple-700 hover:bg-purple-50 disabled:opacity-50"
      >
        {busy ? 'Generating…' : 'Generate IAD'}
      </button>
      {error && (
        <span
          className="ml-2 text-[10px] text-red-700 max-w-md truncate"
          title={error}
        >
          {error}
        </span>
      )}
    </span>
  );
}

function BusinessSummary({ plan }: { plan: ChangePlanItem[] }) {
  const s = deriveSummary(plan);
  const parts = [
    s.tables ? `${s.tables} table${s.tables === 1 ? '' : 's'}/field${s.tables === 1 ? '' : 's'}` : '',
    s.procedures ? `${s.procedures} procedure${s.procedures === 1 ? '' : 's'}` : '',
    s.methods ? `${s.methods} code method${s.methods === 1 ? '' : 's'}` : '',
    s.apps ? `${s.apps} application${s.apps === 1 ? '' : 's'}` : '',
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold text-gray-600">
          What this change means
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-indigo-800">
          {'●'.repeat(s.scale.filled)}
          <span className="text-indigo-300">{'○'.repeat(5 - s.scale.filled)}</span>
          <span className="ml-1">{s.scale.label}</span>
        </span>
      </div>
      <div style={{ fontFamily: TECH_FONT, fontSize: '16px', lineHeight: 1.7, color: '#1f2937' }}>
        <p>
          This change touches <strong>{parts.join(', ') || `${s.total} objects`}</strong>.
          {s.mustChangeTogether > 0 && (
            <>
              {' '}
              <strong>{s.mustChangeTogether}</strong> of them must change together — miss one and
              the data can go wrong quietly.
            </>
          )}
          {s.additions > 0 && <> {s.additions} are new additions rather than edits.</>}
        </p>
        {s.biggest && (s.biggest.impact_count ?? 0) > 0 && (
          <p className="mt-1.5">
            Largest ripple: <strong>{s.biggest.component}</strong> is used by{' '}
            <strong>{s.biggest.impact_count}</strong> other objects, so editing it carries the most
            risk of breaking something unrelated.
          </p>
        )}
        {s.unverified > 0 && (
          <p className="mt-1.5 text-[15px] text-gray-600">
            {s.unverified} of {s.total} items are not fully confirmed — treat those as leads to
            check, not facts.
          </p>
        )}
      </div>
      <p className="text-[10px] text-indigo-500">
        Counted directly from the analysis below — no summarising step that could add or lose
        anything.
      </p>
    </div>
  );
}

/** Impact graph — auto-loaded the moment a completed analysis is on screen.
 *  No button, no click gating (Anthony explicit). Tight subgraph of only the
 *  change_plan entities and their direct links; application/other entries
 *  arrive as isolated nodes by design (Compass thread change-graph #43). */
function ChangeImpactGraph({ plan }: { plan: ChangePlanItem[] }) {
  const [graph, setGraph] = useState<ChangeGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setGraph(null);
    setError(null);
    if (!plan.length) return;
    setLoading(true);
    fetchChangeGraph(plan)
      .then((g) => { if (!cancelled) setGraph(g); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [plan]);

  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [maximized]);

  // change_plan action=add → to-be-created entity → dotted node.
  const dashedNames = plan.filter((p) => p.action === 'add').map((p) => p.component);

  const graphView = graph && graph.nodes.length > 0 && (
    <SchemaGraphPage
      graphData={{ nodes: graph.nodes, edges: graph.edges }}
      isModal={true}
      hideSchemaFilters={true}
      dashedNames={dashedNames}
      onMaximize={maximized ? undefined : () => setMaximized(true)}
    />
  );

  return (
    <div className="border-t border-purple-100 pt-2">
      <div className="flex items-center gap-1 text-xs font-medium text-purple-700">
        Impact Graph
        <span className="text-[10px] font-normal text-purple-400">
          entities named by this analysis and their direct links — dotted = to be created
        </span>
      </div>
      {loading && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-purple-500">
          <RefreshCw size={10} className="animate-spin" /> loading graph…
        </div>
      )}
      {error && (
        <p className="mt-2 text-[11px] text-gray-500 italic">Impact graph unavailable: {error}</p>
      )}
      {!maximized && graphView && (
        <div className="mt-2 border border-purple-100 rounded overflow-hidden" style={{ height: 480 }}>
          {graphView}
        </div>
      )}
      {maximized && graphView && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setMaximized(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full flex flex-col" style={{ height: '92vh', maxWidth: '96vw' }}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                <span className="text-sm font-semibold text-gray-900">Impact Graph</span>
                <button
                  onClick={() => setMaximized(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 text-sm font-medium"
                  title="Close (Esc)"
                >
                  ✕ Close
                </button>
              </div>
              <div className="flex-1 overflow-hidden">{graphView}</div>
            </div>
          </div>
        </>
      )}
      {graph && graph.nodes.length === 0 && (
        <p className="mt-2 text-[11px] text-gray-500 italic">No graph nodes for this change plan.</p>
      )}
    </div>
  );
}

function ChangePlanTable({ plan }: { plan: ChangePlanItem[] }) {
  // Impact-first ordering: measured counts descending, unmeasured last.
  const sorted = [...plan].sort(
    (a, b) => (b.impact_count ?? -1) - (a.impact_count ?? -1),
  );
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold">Component</th>
            <th className="px-2 py-1.5 text-left font-semibold">Kind</th>
            <th className="px-2 py-1.5 text-left font-semibold">Action</th>
            <th className="px-2 py-1.5 text-right font-semibold" title="Graph-measured dependents (null = not measured)">Impact</th>
            <th className="px-2 py-1.5 text-left font-semibold">Confidence</th>
            <th className="px-2 py-1.5 text-left font-semibold">Reason</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((item, i) => (
            <tr key={`${item.component}-${i}`} className="border-t border-gray-100 align-top">
              <td className="px-2 py-1.5 font-mono text-[11px] text-gray-800">{item.component}</td>
              <td className="px-2 py-1.5 text-gray-600">{item.kind}</td>
              <td className="px-2 py-1.5 text-gray-800 font-medium">{item.action}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-800">
                {item.impact_count ?? '—'}
              </td>
              <td className="px-2 py-1.5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${CONFIDENCE_STYLE[item.confidence] || CONFIDENCE_STYLE.unverified}`}>
                  {item.confidence}
                </span>
              </td>
              <td className="px-2 py-1.5 text-gray-600">{item.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CrAnalysisPage({ onBack }: { onBack: () => void }) {
  const [crKey, setCrKey] = useState('');
  const [record, setRecord] = useState<CrRecord | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const [list, setList] = useState<CrListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — the query actually sent. Typing must not
  // fire a request per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const analysis: CompassAnalysis | null = record?.compass_response ?? null;

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // A narrower result set makes the current offset meaningless, and an
  // out-of-range offset returns an empty page that reads as "no CRs".
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // The rail is best-effort: a failure here must never block the page.
  const refreshList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await listCrs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        q: searchQuery || undefined,
      });
      setList(data.items);
      setTotal(data.total);
    } catch {
      /* ignored */
    } finally {
      setListLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIdx = Math.min(total, (page + 1) * PAGE_SIZE);

  // Deleting the last row on the last page leaves the offset past the end,
  // which returns an empty page that reads as "no CRs". Step back instead.
  useEffect(() => {
    if (!listLoading && page > totalPages - 1) setPage(totalPages - 1);
  }, [listLoading, page, totalPages]);

  // A newly fetched or analysed CR sorts to the top, so it is on page 0 —
  // refreshing page 3 would reload a page it cannot appear on. If already on
  // page 0 the page-change effect won't fire, so reload explicitly.
  const showNewestFirst = useCallback(() => {
    if (page === 0) refreshList();
    else setPage(0);
  }, [page, refreshList]);

  const [selector, setSelector] = useState<CrAttachmentList | null>(null);

  /** Fresh JIRA pull, gated by the attachment picker: list the names first
   *  (nothing scanned), open the picker when the CR has attachments, go
   *  straight to the full fetch when it has none. */
  const openSelector = async (key: string) => {
    const names = await listCrAttachments(key);
    if (names.attachments.length === 0) {
      setRecord(await fetchCr(key));
      showNewestFirst();
    } else {
      setSelector(names);
    }
  };

  /** Continue from the picker — the real fetch/extract, selection applied. */
  const continueWithSelection = async (ids: string[]) => {
    if (!selector) return;
    const key = selector.cr_key;
    setSelector(null);
    setFetching(true);
    setFetchError(null);
    try {
      setRecord(await fetchCr(key, ids));
      showNewestFirst();
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : String(e));
    } finally {
      setFetching(false);
    }
  };

  const loadKey = async (key: string) => {
    setCrKey(key);
    setFetching(true);
    setFetchError(null);
    try {
      const stored = await getCr(key).catch(() => null);
      if (stored && stored.found !== false && stored.cr_text) setRecord(stored);
      else await openSelector(key);
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : String(e));
    } finally {
      setFetching(false);
    }
  };

  const removeCr = async (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${key} and its stored analysis?`)) return;
    try {
      await deleteCr(key);
      if (record?.cr_key === key) setRecord(null);
      refreshList();
    } catch {
      /* ignored — the row simply stays */
    }
  };

  const load = async () => {
    const key = crKey.trim().toUpperCase();
    if (!key) return;
    // Answer immediately rather than spending a round-trip to be told no.
    // The server enforces this too — this is for speed, not for safety.
    if (!key.startsWith(`${CR_PROJECT}-`)) {
      setFetchError(
        `CR Analysis reads ${CR_PROJECT} tickets only. '${key.split('-')[0]}' looks like a support ticket — use Issue Analysis for that.`,
      );
      setRecord(null);
      return;
    }
    setFetching(true);
    setFetchError(null);
    try {
      // Stored copy first (instant); fall back to a fresh JIRA pull.
      const stored = await getCr(key).catch(() => null);
      if (stored && stored.found !== false && stored.cr_text) {
        setRecord(stored);
        showNewestFirst();
      } else {
        await openSelector(key);
      }
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : String(e));
      setRecord(null);
    } finally {
      setFetching(false);
    }
  };

  const refetch = async () => {
    if (!record) return;
    setFetching(true);
    setFetchError(null);
    try {
      await openSelector(record.cr_key);
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : String(e));
    } finally {
      setFetching(false);
    }
  };

  /** Submit-and-poll: the backend owns the run and persists it, so a closed
   *  tab, a refresh or any proxy timeout no longer discards paid work. A
   *  second submit for the same CR joins the running one. */
  const runAnalysis = async (existingJob = false) => {
    if (!record) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setElapsed(0);
    try {
      if (!existingJob) {
        const { deduped } = await submitAnalysis('change', record.cr_key, record.cr_text);
        if (deduped) setAnalyzeError(null);
      }
      const job = await pollJob('change', record.cr_key, { onTick: setElapsed });
      if (job.status === 'done' && job.response) {
        setRecord((r) =>
          r ? { ...r, compass_response: job.response!, analyzed_at: new Date().toISOString() } : r,
        );
        showNewestFirst();
      } else {
        // Say what actually failed. "Compass analysis failed" for our own
        // stopped-waiting case names a healthy service and sends the next
        // person debugging in the wrong direction (Compass, mesh #27).
        setAnalyzeError(
          job.error?.includes('CLIENT_GAVE_UP')
            ? 'Still running — this CR is taking longer than usual. It has not failed; wait a moment and press Re-run to pick up the result.'
            : job.error?.includes('429')
              ? 'Rate limit (1 CR analysis per minute) — try again shortly.'
              : job.error || 'The analysis did not complete.',
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setAnalyzeError(
        msg.includes('429')
          ? 'Rate limit (1 CR analysis per minute) — try again shortly.'
          : /\b(502|503|504)\b/.test(msg)
            ? 'The analysis service could not be reached. Try again shortly.'
            : `Analysis could not be started: ${msg}`,
      );
    } finally {
      setAnalyzing(false);
    }
  };

  // NOTE: there is deliberately no client-side filter here. Search runs on
  // the server, so it covers every stored CR; filtering the loaded page as
  // well would silently hide matches on other pages.

  return (
    // Violet wash distinguishes CR Analysis from Issue Analysis at a glance —
    // the two pages are structurally similar and easy to confuse.
    <div className="flex-1 flex overflow-hidden bg-violet-50/40">
      {/* ── Stored CRs rail (mirrors Issue Analysis) ───────────────── */}
      <aside className="w-72 shrink-0 border-r border-violet-200 bg-white/80 flex flex-col">
        <div className="p-2 border-b border-violet-100">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search CR or summary…"
              className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto">
          {listLoading && list.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-400 text-center">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-400 text-center">
              {search ? 'No matches' : 'No CRs loaded yet'}
            </div>
          ) : (
            <div className="py-1">
              {list.map((c) => {
                const cx = complexityOf(c.items, c.max_impact);
                return (
                  <button
                    key={c.cr_key}
                    onClick={() => loadKey(c.cr_key)}
                    className={`group w-full text-left px-3 py-2 hover:bg-violet-50 transition-colors border-b border-gray-50 ${
                      record?.cr_key === c.cr_key ? 'bg-violet-50 border-l-2 border-l-purple-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-800 font-mono">{c.cr_key}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">
                          {formatTime(c.analyzed_at || c.fetched_at)}
                        </span>
                        <button
                          onClick={(e) => removeCr(e, c.cr_key)}
                          className="p-0.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete this CR and its analysis"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5">{c.summary}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {c.analyzed_at ? (
                        <span
                          className={`flex items-center gap-1 px-1 rounded text-[10px] ${cx.chip}`}
                          title={`${c.items} items, largest ripple ${c.max_impact}`}
                        >
                          <span>{cx.dot}</span>
                          {cx.label}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400 italic">not analysed</span>
                      )}
                      {c.elapsed_ms > 0 && (
                        <span className="text-[10px] text-gray-400">
                          {c.elapsed_ms >= 60000
                            ? `${(c.elapsed_ms / 60000).toFixed(1)}m`
                            : `${(c.elapsed_ms / 1000).toFixed(0)}s`}
                        </span>
                      )}
                      {c.attachment_issues > 0 && (
                        <span
                          className="text-[10px] text-red-500 flex items-center gap-0.5"
                          title={`${c.attachment_issues} attachment(s) unreadable, oversize, or over the limit — old .xls/.doc need converting`}
                        >
                          <XCircle size={9} />
                          {c.attachment_issues}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </nav>
        {total > 0 && (
          <div className="border-t border-violet-100 px-2 py-1.5 flex items-center justify-between">
            <span className="text-[10px] text-gray-500 tabular-nums">
              {startIdx}–{endIdx} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || listLoading}
                title="Previous page"
                className="p-1 rounded text-gray-500 hover:bg-violet-50 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={13} />
              </button>
              <span className="text-[10px] text-gray-500 tabular-nums">
                {page + 1}/{totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || listLoading}
                title="Next page"
                className="p-1 rounded text-gray-500 hover:bg-violet-50 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-200 text-gray-500" title="Back to chat">
            <ArrowLeft size={16} />
          </button>
          <FileSearch size={18} className="text-purple-600" />
          <h1 className="text-lg font-semibold text-gray-800">CR Analysis</h1>
          <span className="text-xs text-gray-500">
            — change-request impact analysis over the LRP code &amp; schema graph
          </span>
        </div>

        {/* CR key input */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2">
          <input
            value={crKey}
            onChange={(e) => setCrKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !fetching && load()}
            placeholder="LRPP ticket key (e.g. LRPP-1234)"
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-purple-400"
          />
          <button
            onClick={load}
            disabled={fetching || !crKey.trim()}
            className="px-3 py-1.5 text-sm font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {fetching ? <Loader2 size={14} className="animate-spin" /> : 'Load CR'}
          </button>
        </div>
        {fetchError && (
          <p className="text-xs text-red-600">{fetchError}</p>
        )}

        {/* Fetched content */}
        {record && (
          <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">{record.cr_key}</span>
              <span className="text-sm text-gray-600 truncate">{record.summary}</span>
              <span className="ml-auto text-[10px] text-gray-400 whitespace-nowrap">
                {record.char_count.toLocaleString()} chars
              </span>
              <button
                onClick={refetch}
                disabled={fetching}
                className="flex items-center gap-0.5 text-[10px] text-purple-600 hover:text-purple-800"
                title="Re-pull the CR from JIRA (keeps the stored analysis)"
              >
                <RefreshCw size={10} className={fetching ? 'animate-spin' : ''} /> Refresh from JIRA
              </button>
              <button
                onClick={() =>
                  downloadAnalysisJson({ kind: 'cr-analysis', key: record.cr_key }, record)
                }
                className="flex items-center gap-0.5 text-[10px] text-purple-600 hover:text-purple-800"
                title="Download this CR and its analysis as JSON — includes customer data"
              >
                <Download size={10} /> JSON
              </button>
            </div>

            {/* Attachment statuses */}
            {record.extraction_results.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {record.extraction_results.map((r, i) => (
                  <span
                    key={`${r.filename}-${i}`}
                    title={r.detail || r.status}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] ${
                      r.status === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : r.status === 'failed'
                          ? 'bg-red-50 text-red-600 border border-red-200'
                          : 'bg-gray-50 text-gray-500 border border-gray-200'
                    }`}
                  >
                    {r.status === 'success' ? <CheckCircle size={9} /> : <AlertCircle size={9} />}
                    <Paperclip size={9} />
                    {r.filename}
                  </span>
                ))}
              </div>
            )}

            <CoverageNotice items={crCoverage(record)} limits={CR_LIMITS} mode="cr" />

            {/* Full assembled text (what Compass receives) */}
            <div>
              <button
                onClick={() => setTextOpen((v) => !v)}
                className="flex items-center gap-1 text-[11px] font-medium text-gray-600"
              >
                {textOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Full CR text (exactly what the analysis receives)
              </button>
              {textOpen && (
                <div className="mt-1 p-2 bg-green-50 rounded border border-green-200 text-[11px] text-gray-700 max-h-72 overflow-y-auto whitespace-pre-wrap font-mono">
                  {record.cr_text}
                </div>
              )}
            </div>

            {/* Analyze — the button stays VISIBLE and disabled while running,
                rather than disappearing: a vanished button leaves the user
                unsure the click registered, which is what produced duplicate
                paid runs. The backend dedupe still covers a retry after an
                apparent failure, which no disabled state can prevent. */}
            {!analysis && (
              <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                <button
                  onClick={() => runAnalysis()}
                  disabled={analyzing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-wait"
                >
                  {analyzing ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Analyzing…{' '}
                      {elapsed > 0 && <span className="tabular-nums">{elapsed}s</span>}
                    </>
                  ) : (
                    <>
                      <Compass size={14} /> Run impact analysis (Compass)
                    </>
                  )}
                </button>
                <span className="text-[10px] text-gray-500">
                  {analyzing
                    ? 'Running against 18 applications, 7.6K procedures and the full schema — typically 2–4 minutes. Safe to leave this page; the result is saved.'
                    : 'Deep analysis over the code + schema graph — takes 2–4 minutes, paid AI call.'}
                </span>
              </div>
            )}
            {analyzeError && !analyzing && (
              <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                <p className="text-xs text-gray-500 italic">{analyzeError}</p>
                <button onClick={() => runAnalysis()} className="text-[10px] text-purple-600 underline">
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* Analysis result */}
        {record && analysis && (
          <div className="bg-white border border-purple-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-purple-100 flex items-center gap-2">
              <Compass size={16} className="text-purple-700" />
              <span className="text-sm font-semibold text-purple-900">Impact Analysis</span>
              <GenerateIadButton
                crKey={record.cr_key}
                crText={record.cr_text}
                explanation={analysis.explanation}
                changePlan={analysis.change_plan}
                degraded={!!analysis.degraded}
              />
              <span className="ml-auto flex items-center gap-2 text-[10px] text-purple-500">
                {record.analyzed_at && (
                  <span title={record.analyzed_at}>
                    analyzed {new Date(record.analyzed_at).toLocaleString()}
                  </span>
                )}
                {emailName(record.analyzed_by) && (
                  <span
                    className="font-medium text-purple-700"
                    title={record.analyzed_by ?? undefined}
                  >
                    {emailName(record.analyzed_by)}
                  </span>
                )}
                {analysis.model && !analysis.degraded && (
                  <span>{analysis.model} · {(analysis.elapsed_ms / 1000).toFixed(0)}s</span>
                )}
                <button
                  onClick={() => runAnalysis()}
                  disabled={analyzing}
                  className="flex items-center gap-0.5 text-purple-600 hover:text-purple-800"
                  title="Fresh analysis (2–4 min, paid) — replaces the stored result"
                >
                  <RefreshCw size={10} className={analyzing ? 'animate-spin' : ''} /> Re-run
                </button>
              </span>
            </div>
            <div className="px-4 py-3 space-y-3">
              {analysis.degraded && (
                <div className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded p-1.5">
                  AI analysis unavailable — showing knowledge-graph facts.
                </div>
              )}

              {/* Business tier — always visible, derived by counting, never
                  paraphrased. Absent when degraded (change_plan is null). */}
              {analysis.change_plan && analysis.change_plan.length > 0 && (
                <BusinessSummary plan={analysis.change_plan} />
              )}

              {/* Developer tier — collapsed. Nothing is removed; a business
                  reader simply is not made to scroll past it. */}
              {analysis.change_plan && analysis.change_plan.length > 0 && (
                <div className="border-t border-purple-100 pt-2">
                  <button
                    onClick={() => setPlanOpen((v) => !v)}
                    className="flex items-center gap-1 text-xs font-medium text-purple-700"
                  >
                    {planOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Change Plan — {analysis.change_plan.length} items, impact-first
                    <span className="text-[10px] font-normal text-purple-400">for developers</span>
                  </button>
                  {planOpen && (
                    <div className="mt-2">
                      <ChangePlanTable plan={analysis.change_plan} />
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-purple-100 pt-2">
                <button
                  onClick={() => setTechOpen((v) => !v)}
                  className="flex items-center gap-1 text-xs font-medium text-purple-700"
                >
                  {techOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Full technical analysis
                  <span className="text-[10px] font-normal text-purple-400">for developers</span>
                </button>
                {(techOpen || !analysis.change_plan?.length) && (
                  <div
                    className="mt-2"
                    style={{ fontFamily: TECH_FONT, fontSize: '16px', lineHeight: 1.7, color: '#1f2937' }}
                  >
                    <MarkdownRenderer content={analysis.explanation} showTableOfContents={false} />
                  </div>
                )}
              </div>

              {analysis.change_plan && analysis.change_plan.length > 0 && (
                <ChangeImpactGraph plan={analysis.change_plan} />
              )}

              <CompassEvidenceAccordion evidence={analysis.evidence} borderClass="border-purple-100" />
            </div>
          </div>
        )}
      </div>
      </div>

      {selector && (
        <AttachmentSelectorModal
          data={selector}
          onContinue={continueWithSelection}
          onCancel={() => setSelector(null)}
        />
      )}
    </div>
  );
}
